<template>
    <div class="kpi-grid">
        <div class="kpi-card">
            <div class="kpi-label">Zamówienia ({{ rangeLabel }})</div>
            <div class="kpi-value">{{ kpi.orders }}</div>
            <div class="kpi-sub">Dziś: {{ kpi.ordersToday }}</div>
        </div>

        <div class="kpi-card">
            <div class="kpi-label">Przychód ({{ rangeLabel }})</div>
            <div class="kpi-value">{{ formatPLN(kpi.revenue) }}</div>
            <div class="kpi-sub">Śr. koszyk: {{ formatPLN(kpi.aov) }}</div>
        </div>

        <div class="kpi-card">
            <div class="kpi-label">Najpopularniejszy model</div>
            <div class="kpi-value kpi-ellipsis">{{ kpi.topTrailer?.name || '—' }}</div>
            <div class="kpi-sub">
                {{ kpi.topTrailer ? kpi.topTrailer.count + ' zam.' : 'Brak danych' }}
            </div>
        </div>

        <div class="kpi-card">
            <div class="kpi-label">Klienci</div>
            <div class="kpi-value">
                {{ kpi.shareCompany }}% / {{ 100 - kpi.shareCompany }}%
            </div>
            <div class="kpi-sub">Firma / Osoba prywatna</div>
        </div>
    </div>
</template>

<script>
import { useOrderStore } from '@/stores/orderStore'
import { useTrailerStore } from '@/stores/trailerStore'
import { useAddonForModelStore } from '@/stores/addonForModelStore'

export default {
    props: {
        selectedRange: { type: String, required: true }
    },
    data() {
        return {
            orderStore: useOrderStore(),
            trailerStore: useTrailerStore(),
            afmStore: useAddonForModelStore()
        }
    },
    computed: {
        orders() {
            return this.orderStore.orders || []
        },
        trailerMap() {
            const map = new Map()
            for (const t of this.trailerStore.trailers) map.set(t.id, t)
            return map
        },
        afmPriceIndex() {
            const idx = new Map()
            for (const r of this.afmStore.addonsForModels) {
                const key = `${r.trailer_id}|${r.addon_id}`
                const val = Math.round(parseFloat(r.price || 0) * 100)
                idx.set(key, val)
            }
            return idx
        },
        rangeLabel() {
            const map = {
                '7d': '7 dni',
                '30d': '30 dni',
                '90d': '90 dni',
                '180d': '180 dni',
                'ytd': 'Bieżący rok',
                '1y': 'Ostatni rok',
                'all': 'Wszystko'
            }
            return map[this.selectedRange] || '—'
        },
        fromDate() {
            const now = new Date()
            const clampStart = (days) => {
                const d = new Date()
                d.setDate(d.getDate() - (days - 1))
                d.setHours(0, 0, 0, 0)
                return d
            }
            if (this.selectedRange === '7d') return clampStart(7)
            if (this.selectedRange === '30d') return clampStart(30)
            if (this.selectedRange === '90d') return clampStart(90)
            if (this.selectedRange === '180d') return clampStart(180)
            if (this.selectedRange === '1y') {
                const d = new Date()
                d.setFullYear(d.getFullYear() - 1)
                d.setHours(0, 0, 0, 0)
                return d
            }
            if (this.selectedRange === 'ytd') {
                const d = new Date(now.getFullYear(), 0, 1)
                d.setHours(0, 0, 0, 0)
                return d
            }
            const minDate = this.orders.length
                ? new Date(Math.min(...this.orders.map(o => +new Date(o.created_at))))
                : clampStart(30)
            minDate.setHours(0, 0, 0, 0)
            return minDate
        },
        ordersInRange() {
            return this.orders.filter(o => new Date(o.created_at) >= this.fromDate)
        },
        kpi() {
            const count = this.ordersInRange.length
            const revenue = this.ordersInRange.reduce((sum, o) => sum + this.orderTotal(o), 0)
            const todayStr = new Date().toISOString().slice(0, 10)
            const ordersToday = this.orders.filter(o => (o.created_at || '').slice(0, 10) === todayStr).length

            const freq = new Map()
            for (const o of this.ordersInRange) {
                const id = o.trailer_id
                freq.set(id, (freq.get(id) || 0) + 1)
            }
            const topEntry = [...freq.entries()].sort((a, b) => b[1] - a[1])[0]
            const topTrailer = topEntry
                ? { id: topEntry[0], name: this.getTrailerName(topEntry[0]), count: topEntry[1] }
                : null

            const companies = this.ordersInRange.filter(o => o.customer_type === 'company').length
            const shareCompany = count ? Math.round(companies / count * 100) : 0

            return {
                orders: count,
                revenue,
                aov: count ? Math.round(revenue / count) : 0,
                ordersToday,
                topTrailer,
                shareCompany
            }
        }
    },
    methods: {
        getTrailerName(id) {
            return this.trailerMap.get(id)?.name || '—'
        },
        orderTotal(order) {
            const trailerPrice = this.trailerMap.get(order.trailer_id)?.price || 0
            const addonIds = order.addon_ids || []
            const addonsSum = addonIds.reduce((sum, aid) => {
                const key = `${order.trailer_id}|${aid}`
                return sum + (this.afmPriceIndex.get(key) || 0)
            }, 0)
            return trailerPrice + addonsSum
        },
        formatPLN(cents) {
            return (cents / 100).toLocaleString('pl-PL', {
                style: 'currency',
                currency: 'PLN',
                minimumFractionDigits: 2
            })
        }
    },
    created() {
        if (!this.orderStore.orders?.length) this.orderStore.fetchOrders?.()
        if (!this.trailerStore.trailers?.length) this.trailerStore.fetchTrailers?.()
        if (!this.afmStore.addonsForModels?.length) this.afmStore.fetchAll?.()
    }
}
</script>

<style scoped lang="scss">
.kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-bottom: 1rem;

    @media (max-width: 1000px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
}

.kpi-card {
    background: $color-white;
    border: 1px solid $color-grey;
    border-radius: 10px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
}

.kpi-label {
    font-size: 0.9rem;
    color: $color-text-light;
}

.kpi-value {
    font-size: 1.6rem;
    font-weight: 700;
    color: $color-primary-dark;
    line-height: 1.2;
}

.kpi-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.kpi-sub {
    font-size: 0.85rem;
    color: $color-text-light;
}
</style>